import { Bar, LibrarySymbolInfo, Position, SeriesType } from '@frontends/feature-charting-library/charting_library';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { type AppThunk, type RootState } from '../../app/rootState';
import { resolutionToSeconds } from '../../components/tradingView/lib/helpers';
import { $axios } from '../../hooks/useAxiosInterceptors';
import { getEntityConfig } from '../../hooks/useEntityConfig';
import { selectAccount, selectSymbols } from '../initialSlice/initialSlice';

export type SessionStatistics = {
  bestTrade: number;
  worstTrade: number;
  avgTrade: number;
  totalTrades: number;
  winRate: number;
  duration: number;
  totalPNL: number;
  totalPNLPercent: number;
  equityTimeline: RealizedPNLPoint[];
  selectedAsset: LibrarySymbolInfo | null;
};
export type RealizedPNLPoint = {
  time: number;
  equity: number;
};

export interface MainState {
  appliedFromSec: number | null;
  appliedToSec: number | null;
  balance: number;
  chartRes: string;
  chartSpeed: number;
  chartType: SeriesType;
  crossCurrencyData: Record<number, number>;
  // quotesMap: Map<string, Bar[]>
  currentDate: number;
  dateChangeCounter: number;
  initialDate: number;
  isFinishReached: boolean;
  isPanelBlocked: boolean;
  isPaused: boolean;
  isReplayStarted: boolean;
  maxDate: number;
  positionHistory: Position[];
  rangeFromSec: number;
  sessionStatistic: SessionStatistics | null;
  stepRes: string;
  symbol: LibrarySymbolInfo | null;
}

const defaultInitialDate: MainState = {
  currentDate: Math.floor(Date.now() / 1000) - 86400 * 30,
  maxDate: Math.floor(Date.now() / 1000),
  stepRes: '240',
  chartRes: '240',
  isPaused: true,
  dateChangeCounter: 0,
  isFinishReached: false,
  chartSpeed: 1,
  isPanelBlocked: false,
  balance: 1000,
  rangeFromSec: 0,
  symbol: null,
  crossCurrencyData: {},
  positionHistory: [],
  sessionStatistic: null,
  appliedFromSec: null,
  appliedToSec: null,
  initialDate: Math.floor(Date.now() / 1000) - 86400 * 30,
  isReplayStarted: false,
  chartType: Number(localStorage.getItem('tradingview.chart.lastUsedStyle')),
};

const initialState: MainState = {
  ...defaultInitialDate,
};

const fetchSymbolBars = async (tradingAccountId: string | undefined, symbol: string): Promise<Bar[]> => {
  if (!tradingAccountId) return [];

  try {
    const entityConfig = getEntityConfig();
    const to = Math.floor(Date.now() / 1000);
    const from = to - 3 * 365.25 * 24 * 60 * 60;
    const query = `timeframe=${86400 / 60}&from_time=${from}&to_time=${to}`;
    const response = await $axios.get(
      `${entityConfig.VITE_URL_CHART_SERVICE}/api/v1/trading/accounts/${tradingAccountId}/symbols/${encodeURIComponent(symbol)}/charts?${query}`,
    );

    return response.data as Bar[];
  } catch {
    return [];
  }
};

export const changeSymbol =
  (symbol: LibrarySymbolInfo): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setSymbol(symbol));

    const symbolsArray = selectSymbols(getState());
    const hasConversionPair = (symbolName: string): boolean =>
      symbolsArray.some((i: LibrarySymbolInfo) => i.name.toLowerCase() === symbolName.toLowerCase());
    const currencyConversion = 'USD';
    const account = selectAccount(getState());
    const accountCurrency = account?.currency;
    const tradingAccountId = account?.login;

    const customFields = (symbol.library_custom_fields ?? {}) as Record<string, unknown>;
    const currencyProfit = customFields.currency_profit as string | undefined;

    let toReversed = false;
    let fromReversed = false;

    let rates1Promise: Promise<Bar[]> | null = null;
    let rates2Promise: Promise<Bar[]> | null = null;

    if (currencyProfit === accountCurrency) return;

    if (accountCurrency !== currencyConversion) {
      if (hasConversionPair(`${currencyConversion}${accountCurrency}`)) {
        rates2Promise = fetchSymbolBars(tradingAccountId, `${currencyConversion}${accountCurrency}`);
      } else if (hasConversionPair(`${accountCurrency}${currencyConversion}`)) {
        rates2Promise = fetchSymbolBars(tradingAccountId, `${accountCurrency}${currencyConversion}`);
        toReversed = true;
      }
    }

    if (currencyProfit !== currencyConversion) {
      if (hasConversionPair(`${currencyProfit}${currencyConversion}`)) {
        rates1Promise = fetchSymbolBars(tradingAccountId, `${currencyProfit}${currencyConversion}`);
      } else if (hasConversionPair(`${currencyConversion}${currencyProfit}`)) {
        rates1Promise = fetchSymbolBars(tradingAccountId, `${currencyConversion}${currencyProfit}`);
        fromReversed = true;
      }
    }

    const [rates1, rates2] = await Promise.all([
      rates1Promise ?? Promise.resolve([]),
      rates2Promise ?? Promise.resolve([]),
    ]);

    const buildDayRateMap = (bars: Bar[], reversed: boolean): Record<number, number> => {
      const map: Record<number, number> = {};

      for (const bar of bars) {
        const dayTs = Math.floor(bar.time / 1000 / 86400) * 86400;
        map[dayTs] = reversed ? 1 / bar.close : bar.close;
      }

      return map;
    };

    const map1 = buildDayRateMap(rates1, fromReversed);
    const map2 = buildDayRateMap(rates2, toReversed);

    const todayTs = Math.floor(Date.now() / 1000 / 86400) * 86400;
    const fromTs = todayTs - Math.floor(5 * 365.25) * 86400;
    const crossCurrency: Record<number, number> = {};
    let prevR1 = 1;
    let prevR2 = 1;

    for (let day = fromTs; day <= todayTs; day += 86400) {
      const r1 = map1[day] ?? prevR1;
      const r2 = map2[day] ?? prevR2;
      prevR1 = r1;
      prevR2 = r2;
      crossCurrency[day] = r1 * r2;
    }

    dispatch(setCrossCurrencyData(crossCurrency));
  };

export const mainSlice = createSlice({
  name: 'mainSlice',
  initialState,
  reducers: {
    changeCurrentDate: (state, action: PayloadAction<number>) => {
      const upperBound = state.maxDate + resolutionToSeconds[state.stepRes];
      state.currentDate = Math.min(action.payload, upperBound);
    },
    changeMaxDate: (state, action: PayloadAction<number>) => {
      state.maxDate = action.payload;
    },
    setIsPaused: (state, action: PayloadAction<boolean>) => {
      state.isPaused = action.payload;
    },
    setStepRes: (state, action: PayloadAction<string>) => {
      state.stepRes = action.payload;
    },
    setChartRes: (state, action: PayloadAction<string>) => {
      state.chartRes = action.payload;
    },
    triggerDateChange: (state) => {
      state.dateChangeCounter += 1;
    },
    setIsFinishReached: (state, action: PayloadAction<boolean>) => {
      state.isFinishReached = action.payload;
    },
    setChartSpeed: (state, action: PayloadAction<number>) => {
      state.chartSpeed = action.payload;
    },
    setPanelBlocked: (state, action: PayloadAction<boolean>) => {
      state.isPanelBlocked = action.payload;
    },
    setBalance: (state, action: PayloadAction<number>) => {
      state.balance = action.payload;
    },
    setRangeFromSec: (state, action: PayloadAction<number>) => {
      state.rangeFromSec = action.payload;
    },
    setSymbol: (state, action: PayloadAction<LibrarySymbolInfo>) => {
      state.symbol = { ...action.payload };
    },
    setCrossCurrencyData: (state, action: PayloadAction<Record<number, number>>) => {
      state.crossCurrencyData = action.payload;
    },
    addPositionToHistory: (state, action: PayloadAction<Position>) => {
      state.positionHistory.push({ ...action.payload });
    },
    clearPositionHistory: (state) => {
      state.positionHistory = [];
    },
    setSessionStatistic: (state, action: PayloadAction<SessionStatistics | null>) => {
      state.sessionStatistic = action.payload;
    },
    setAppliedFromSec: (state, action: PayloadAction<number | null>) => {
      state.appliedFromSec = action.payload;
    },
    setAppliedToSec: (state, action: PayloadAction<number | null>) => {
      state.appliedToSec = action.payload;
    },
    setInitialDate: (state, action: PayloadAction<number>) => {
      const upperBound = state.maxDate + resolutionToSeconds[state.stepRes];
      state.initialDate = Math.min(action.payload, upperBound);
    },
    setIsReplayStarted: (state, action: PayloadAction<boolean>) => {
      state.isReplayStarted = action.payload;
    },
    setChartType: (state, action: PayloadAction<SeriesType>) => {
      state.chartType = action.payload;
    },
  },
});

export const {
  changeCurrentDate,
  changeMaxDate,
  setIsPaused,
  setStepRes,
  setChartRes,
  triggerDateChange,
  setChartSpeed,
  setPanelBlocked,
  setBalance,
  setIsFinishReached,
  setRangeFromSec,
  setSymbol,
  setCrossCurrencyData,
  clearPositionHistory,
  addPositionToHistory,
  setSessionStatistic,
  setAppliedFromSec,
  setAppliedToSec,
  setInitialDate,
  setIsReplayStarted,
  setChartType,
} = mainSlice.actions;

export const resetSession = (): AppThunk => (dispatch, getState) => {
  const { isPaused, initialDate, isReplayStarted, isFinishReached, sessionStatistic, isPanelBlocked } =
    defaultInitialDate;

  const { initialDate: currentInitialDate } = getState().mainSlice;
  const fromSec = currentInitialDate || initialDate;
  const toSec = Math.floor(Date.now() / 1000);

  dispatch(setIsPaused(isPaused));
  dispatch(setIsReplayStarted(isReplayStarted));
  dispatch(setIsFinishReached(isFinishReached));
  dispatch(setPanelBlocked(isPanelBlocked));
  dispatch(changeMaxDate(toSec));
  dispatch(changeCurrentDate(fromSec));
  dispatch(setRangeFromSec(fromSec));
  dispatch(setAppliedFromSec(fromSec));
  dispatch(setAppliedToSec(toSec));
  dispatch(setSessionStatistic(sessionStatistic));
  dispatch(clearPositionHistory());
  dispatch(triggerDateChange());
};
export const selectCurrentDate = (state: RootState): number => state.mainSlice.currentDate;
export const selectMaxDate = (state: RootState): number => state.mainSlice.maxDate;
export const selectIsPaused = (state: RootState): boolean => state.mainSlice.isPaused;
export const selectDateChangeCounter = (state: RootState): number => state.mainSlice.dateChangeCounter;
export const selectIsFinishReached = (state: RootState): boolean => state.mainSlice.isFinishReached;
export const selectStepRes = (state: RootState): string => state.mainSlice.stepRes;
export const selectChartRes = (state: RootState): string => state.mainSlice.chartRes;
export const selectChartSpeed = (state: RootState): number => state.mainSlice.chartSpeed;
export const selectIsPanelBlocked = (state: RootState): boolean => state.mainSlice.isPanelBlocked;
export const selectBalance = (state: RootState): number => state.mainSlice.balance;
export const selectRangeFromSec = (state: RootState): number => state.mainSlice.rangeFromSec;
export const selectSymbol = (state: RootState): LibrarySymbolInfo | null => state.mainSlice.symbol;
export const selectCrossRates = (state: RootState): Record<number, number> => state.mainSlice.crossCurrencyData;
export const selectSessionStatistic = (state: RootState): SessionStatistics | null => state.mainSlice.sessionStatistic;
export const selectPositionHistory = (state: RootState): Position[] => state.mainSlice.positionHistory;
export const selectAppliedFromSec = (state: RootState): number | null => state.mainSlice.appliedFromSec;
export const selectAppliedToSec = (state: RootState): number | null => state.mainSlice.appliedToSec;
export const selectInitialDate = (state: RootState): number => state.mainSlice.initialDate;
export const selectIsReplayStarted = (state: RootState): boolean => state.mainSlice.isReplayStarted;
export const selectChartType = (state: RootState): SeriesType => state.mainSlice.chartType;
export default mainSlice.reducer;
